/**
 * Utility functions for transforming skills data for GalaxyView visualization
 */

/**
 * Normalize occurrence values to a range (e.g., 0-1 or min-max size)
 */
export const normalizeOccurrence = (skills, minSize = 10, maxSize = 100) => {
  if (!skills || skills.length === 0) return skills;

  const getOccurrence = (skill) => skill?.occurrence ?? skill?.occurrenceCount ?? 0;
  const occurrences = skills.map((skill) => getOccurrence(skill)).filter((val) => val > 0);
  
  if (occurrences.length === 0) {
    return skills.map((skill) => ({ ...skill, normalizedSize: minSize }));
  }

  const minOccurrence = Math.min(...occurrences);
  const maxOccurrence = Math.max(...occurrences);
  const range = maxOccurrence - minOccurrence || 1;

  return skills.map((skill) => {
    const occurrence = getOccurrence(skill);
    const normalizedSize =
      occurrence > 0
        ? minSize + ((occurrence - minOccurrence) / range) * (maxSize - minSize)
        : minSize;
    return { ...skill, normalizedSize };
  });
};

/**
 * Calculate relationships between skills based on shared professions
 * For now, we'll create synthetic relationships based on occurrence similarity
 * In a real implementation, this would use actual profession data
 */
export const calculateSkillRelationships = (skills, maxConnections = 5) => {
  const relationships = [];
  const normalizedSkills = normalizeOccurrence(skills, 1, 10);
  const getOccurrence = (skill) => skill?.occurrence ?? skill?.occurrenceCount ?? 0;

  // Create relationships based on occurrence similarity
  // Skills with similar occurrence values are more likely to be related
  for (let i = 0; i < normalizedSkills.length; i++) {
    const skill1 = normalizedSkills[i];
    const connections = [];

    for (let j = i + 1; j < normalizedSkills.length; j++) {
      const skill2 = normalizedSkills[j];
      
      // Calculate similarity score based on occurrence difference
      const occurrenceDiff = Math.abs(getOccurrence(skill1) - getOccurrence(skill2));
      const maxOccurrence = Math.max(getOccurrence(skill1), getOccurrence(skill2)) || 1;
      const similarity = 1 - Math.min(occurrenceDiff / maxOccurrence, 1);

      // Add connection if similarity is above threshold
      if (similarity > 0.3) {
        connections.push({
          target: skill2._id,
          similarity,
          strength: similarity
        });
      }
    }

    // Sort by similarity and take top connections
    connections.sort((a, b) => b.similarity - a.similarity);
    connections.slice(0, maxConnections).forEach((conn) => {
      relationships.push({
        source: skill1._id,
        target: conn.target,
        strength: conn.strength
      });
    });
  }

  return relationships;
};

/**
 * Transform skills for force-directed graph (used by GalaxyView)
 */
export const transformForForceDirected = (skills, relationships) => {
  const normalizedSkills = normalizeOccurrence(skills, 5, 30);
  const getOccurrence = (skill) => skill?.occurrence ?? skill?.occurrenceCount ?? 0;
  
  return {
    nodes: normalizedSkills.map((skill) => ({
      id: skill._id,
      name: skill.name || skill[`name${skill.lang || 'EN'}`] || 'Skill',
      skill: skill,
      radius: skill.normalizedSize || 10,
      occurrence: getOccurrence(skill),
      x: Math.random() * 800,
      y: Math.random() * 600,
      vx: 0,
      vy: 0
    })),
    links: relationships.map((rel) => ({
      source: rel.source,
      target: rel.target,
      strength: rel.strength
    }))
  };
};

